import { useEffect, useRef, useCallback } from 'react';

/**
 * useSSE — connects to Server-Sent Events for real-time pipeline updates.
 * 
 * Usage:
 *   useSSE(pipelineId, {
 *     onCardMoved: () => reload(),
 *     onRatingUpdated: () => reload(),
 *     onFeedbackAdded: () => reload(),
 *     onMeetingCreated: () => reload(),
 *     onStatusChanged: () => reload(),
 *     onStageChanged: () => reload(),
 *   });
 */
export default function useSSE(pipelineId, handlers = {}) {
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const connect = useCallback(() => {
    if (!pipelineId) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    const url = `${window.location.origin}/api/sse/pipeline/${pipelineId}?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const h = handlersRef.current;

        switch (data.type) {
          case 'connected':
            console.log('SSE: Connected to pipeline', data.pipelineId);
            break;
          case 'card_moved':
          case 'card_deleted':
            console.log('SSE: Received', data.type, data);
            h.onCardMoved?.(data);
            break;
          case 'rating_updated':
            console.log('SSE: Received rating_updated', data);
            h.onRatingUpdated?.(data);
            break;
          case 'status_changed':
            console.log('SSE: Received status_changed', data);
            h.onStatusChanged?.(data);
            break;
          case 'feedback_added':
            console.log('SSE: Received feedback_added', data);
            h.onFeedbackAdded?.(data);
            break;
          case 'meeting_created':
          case 'meeting_sent':
          case 'meeting_cancelled':
            h.onMeetingUpdated?.(data);
            break;
          case 'stage_created':
          case 'stage_updated':
          case 'stage_deleted':
          case 'stage_reordered':
            h.onStageChanged?.(data);
            break;
          case 'candidate_reactivated':
            h.onCandidateReactivated?.(data);
            break;
          case 'note_added':
            // Fired by the AI assistant after add_note succeeds. Also
            // delivered when (in the future) note routes broadcast.
            console.log('SSE: Received note_added', data);
            h.onNoteAdded?.(data);
            break;
          case 'tags_updated':
            // Fired by the AI assistant after tag_candidate succeeds.
            console.log('SSE: Received tags_updated', data);
            h.onTagsUpdated?.(data);
            break;
          default:
            // Generic handler for any event
            h.onAny?.(data);
            break;
        }
      } catch (err) {
        console.error('SSE: Error parsing event:', err);
      }
    };

    es.onerror = () => {
      console.log('SSE: Connection lost, reconnecting in 5s...');
      es.close();
      eventSourceRef.current = null;
      // Auto-reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(connect, 5000);
    };
  }, [pipelineId]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);
}
